import apiClient from "@/api/axiosClient";

export interface TenantProfile {
  name: string;
  assetTagPrefix: string;
  authMode: string;
  status: string;
  planName: string;
  trialEndsAt: string | null;
  trialDaysRemaining: number | null;
  createdAt: string;
}

export interface UpdateTenantProfilePayload {
  name: string;
  assetTagPrefix: string;
}

export async function fetchTenantProfile(): Promise<TenantProfile> {
  const { data } = await apiClient.get<TenantProfile>("/tenant");
  return data;
}

export async function updateTenantProfile(payload: UpdateTenantProfilePayload): Promise<TenantProfile> {
  const { data } = await apiClient.put<TenantProfile>("/tenant", payload);
  return data;
}

/** Dummy self-serve checkout - no real payment processor, just flips the tenant's
 * plan directly. Only "Starter"/"Professional" are accepted; Enterprise is sales-led. */
export async function checkoutPlan(planName: string): Promise<TenantProfile> {
  const { data } = await apiClient.post<TenantProfile>("/tenant/checkout", { planName });
  return data;
}

/** Downloads a multi-sheet workbook (Assets, Assignment History, Maintenance, Team)
 * of everything the tenant owns - meant to be taken before deleteTenantAccount below
 * destroys it all. Uses the server's suggested filename (tenant name + date) from the
 * Content-Disposition header when present, since forcing a Blob download client-side
 * otherwise ignores it. */
export async function exportTenantData(): Promise<void> {
  const response = await apiClient.get("/tenant/export", { responseType: "blob" });
  const disposition: string | undefined = response.headers["content-disposition"];
  const match = disposition?.match(/filename="([^"]+)"|filename=([^;]+)/);
  const fileName = match?.[1] ?? match?.[2] ?? "tenant-data-export.xlsx";
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  window.URL.revokeObjectURL(url);
}

/** Permanently deletes the tenant and every row of data it owns - no undo, no
 * recovery window. confirmCompanyName must exactly match the tenant's current name. */
export async function deleteTenantAccount(confirmCompanyName: string): Promise<void> {
  await apiClient.post("/tenant/delete-account", { confirmCompanyName });
}
